import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { fetchEsimBatchAdmin } from "../../services/esimService";
import { Helmet } from "react-helmet";

import sortUp from "../../assets/sort_up.png";
import sortDown from "../../assets/sort_down.png";
import "./EsimStats.css";

const optionalColumns = [
  { key: "imsi", label: "IMSI" },
  { key: "activation_code", label: "Activation Code" },
  { key: "packages_number", label: "Packages Number" },
  { key: "data_usage", label: "Data Usage" },
  { key: "data_limit", label: "Data Limit" },
  { key: "created_at", label: "Created At" },
  { key: "updated_at", label: "Updated At" },
  { key: "last_order_date", label: "Top-Up Date" },
  { key: "last_package_active_before", label: "Last Package Active Before" },
];

const alwaysVisibleColumns = [
  { key: "last_package_name", label: "Package Name" },
  { key: "user_id", label: "User ID" },
  { key: "provider_id", label: "Provider ID" },
  { key: "iccid", label: "ICCID" },
  { key: "status", label: "Status" },
  { key: "installed", label: "Installed" },
  { key: "data_usage_percent", label: "Usage %" },
  { key: "order_date", label: "Order date" },
];

export default function EsimStats() {
  const { token } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("order_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredRecords, setFilteredRecords] = useState(0);
  const [filters, setFilters] = useState({
    iccid: "",
    imsi: "",
    status: "",
    installed: "",
    status: "",
    min_packages_number: "",
    max_packages_number: "",
    provider_id: "",
    user_id: "",
    min_data_usage: "",
    max_data_usage: "",
    min_data_limit: "",
    max_data_limit: "",
    min_data_usage_percent: "",
    max_data_usage_percent: "",
    min_created_at: "",
    max_created_at: "",
    min_updated_at: "",
    max_updated_at: "",
    min_last_order_date: "",
    max_last_order_date: "",
    min_last_package_active_before: "",
    max_last_package_active_before: "",
  });
  const [visibleOptionals, setVisibleOptionals] = useState([]);

  const tableContainerRef = useRef(null);
  const prevVisibleColumnsLength = useRef(0);

  const statusOptions = ["inactive", "active", "terminated", "archived"];
  const nonFilterableColumns = [];

  const dateRangeColumns = [
    "order_date",
    "created_at",
    "updated_at",
    "last_order_date",
    "last_package_active_before",
  ];
  const numericRangeColumns = [
    "data_usage",
    "data_limit",
    "packages_number",
    "data_usage_percent",
  ];
  const tableRef = useRef(null);

  // Build visible columns list
  const visibleColumns = useMemo(() => {
    return [
      ...alwaysVisibleColumns,
      ...optionalColumns.filter((col) => visibleOptionals.includes(col.key)),
    ];
  }, [visibleOptionals]);

  useEffect(() => {
    if (visibleColumns.length > prevVisibleColumnsLength.current) {
      const tableContainer = tableContainerRef.current;
      if (tableContainer) {
        tableContainer.scrollTo({
          left: tableContainer.scrollWidth,
          behavior: "smooth",
        });
      }
    }
    prevVisibleColumnsLength.current = visibleColumns.length;
  }, [visibleColumns]);

  // Params for API call
  const params = useMemo(() => {
    const p = {
      skip: (page - 1) * pageSize,
      limit: pageSize,
      sort_by: sortBy,
      sort_order: sortOrder,
    };

    if (filters.iccid) p.iccid = filters.iccid;
    if (filters.imsi) p.imsi = filters.imsi;
    if (filters.status) p.status = filters.status;
    if (filters.installed !== "") p.installed = filters.installed === "true";

    if (filters.min_packages_number)
      p.min_packages_number = Number(filters.min_packages_number);
    if (filters.max_packages_number)
      p.max_packages_number = Number(filters.max_packages_number);
    if (filters.provider_id) p.provider_id = Number(filters.provider_id);
    if (filters.user_id) p.user_id = filters.user_id;

    if (filters.min_data_usage)
      p.min_data_usage = Number(filters.min_data_usage);
    if (filters.max_data_usage)
      p.max_data_usage = Number(filters.max_data_usage);
    if (filters.min_data_limit)
      p.min_data_limit = Number(filters.min_data_limit);
    if (filters.max_data_limit)
      p.max_data_limit = Number(filters.max_data_limit);
    if (filters.min_data_usage_percent)
      p.min_data_usage_percent = Number(filters.min_data_usage_percent);
    if (filters.max_data_usage_percent)
      p.max_data_usage_percent = Number(filters.max_data_usage_percent);

    if (filters.min_created_at) p.min_created_at = filters.min_created_at;
    if (filters.max_created_at) p.max_created_at = filters.max_created_at;
    if (filters.min_updated_at) p.min_updated_at = filters.min_updated_at;
    if (filters.max_updated_at) p.max_updated_at = filters.max_updated_at;
    if (filters.min_last_order_date)
      p.min_last_order_date = filters.min_last_order_date;
    if (filters.max_last_order_date)
      p.max_last_order_date = filters.max_last_order_date;
    if (filters.min_last_package_active_before)
      p.min_last_package_active_before = filters.min_last_package_active_before;
    if (filters.max_last_package_active_before)
      p.max_last_package_active_before = filters.max_last_package_active_before;

    return p;
  }, [page, pageSize, sortBy, sortOrder, filters]);

  // Fetch data
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchEsimBatchAdmin({
      token,
      skip: (page - 1) * pageSize,
      limit: pageSize,
      sort_by: sortBy,
      sort_order: sortOrder,
      filters,
    })
      .then((data) => {
        setItems(data.items || []);
        setTotalRecords(data.total || 0);
        setFilteredRecords(data.filtered || 0);
      })
      .catch(() => {
        setItems([]);
        setTotalRecords(0);
        setFilteredRecords(0);
      })
      .finally(() => setLoading(false));
  }, [token, page, pageSize, sortBy, sortOrder, filters]);

  function handleSort(colKey) {
    if (sortBy === colKey) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(colKey);
      setSortOrder("asc");
    }
    setPage(1);
  }

  function toggleColumn(colKey) {
    setVisibleOptionals((prev) =>
      prev.includes(colKey)
        ? prev.filter((c) => c !== colKey)
        : [...prev, colKey]
    );
  }

  // Filter input handler
  function onFilterChange(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }

  function resetFilters() {
    setFilters({
      iccid: "",
      imsi: "",
      status: "",
      installed: "",
      provider_id: "",
      user_id: "",
      min_packages_number: "",
      max_packages_number: "",
      min_data_usage_percent: "",
      max_data_usage_percent: "",
      min_data_usage: "",
      max_data_usage: "",
      min_data_limit: "",
      max_data_limit: "",
      min_created_at: "",
      max_created_at: "",
      min_updated_at: "",
      max_updated_at: "",
      min_last_order_date: "",
      max_last_order_date: "",
      min_last_package_active_before: "",
      max_last_package_active_before: "",
    });
    setPage(1);
  }

  function formatDate(dateString) {
    if (!dateString) return "-";
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  function formatNumberNoDecimal(num) {
    if (num === null || num === undefined) return "-";
    return Math.round(num).toString();
  }

  const filteredItems = items.filter((item) => item.last_order_date !== null);

  return (
    <main className="esim-stats-page">
      <Helmet>
        <title>GlobTel eSIM - eSIM Stats</title>
      </Helmet>
      <header className="partner-offers__hero">
        <h1>eSIM Stats</h1>
        <p className="partner-offers__lead">
          View and manage your eSIM stats and usage statistics.
        </p>
      </header>
      <section className="esim-states partner-offers__wrap" ref={tableRef}>
        <div className="po-toolbar">
          <div className="po-sort">
            <span className="po-sort-label">Sort by:</span>
            <div className="select-wrapper">
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
              >
                {[
                  "iccid",
                  "imsi",
                  "status",
                  "installed",
                  "provider_id",
                  "user_id",
                  "created_at",
                  "updated_at",
                  "order_date",
                  "last_order_date",
                  "last_package_active_before",
                  "data_usage",
                  "data_limit",
                  "packages_number",
                  "last_package_name",
                  "data_usage_percent",
                ].map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="select-wrapper" style={{ marginLeft: ".3rem" }}>
              <select
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value);
                  setPage(1);
                }}
              >
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
              </select>
            </div>
          </div>
          <div className="po-stats">
            <span className="po-stat">Total: {totalRecords}</span>
            <span className="po-stat sep"> </span>
            <span className="po-stat">Filtered: {filteredRecords}</span>
            <span className="po-stat sep"> </span>
          </div>
        </div>

        <div className="po-columns-row">
          <div className="po-columns">
            {optionalColumns.map((col) => (
              <button
                key={col.key}
                type="button"
                className={`column-toggle-btn ${
                  visibleOptionals.includes(col.key) ? "active" : ""
                }`}
                onClick={() => toggleColumn(col.key)}
                aria-pressed={visibleOptionals.includes(col.key)}
              >
                + {col.label}
              </button>
            ))}
          </div>
          <div className="po-pagesize">
            <span className="po-pagesize-label">Rows:</span>
            <div className="select-wrapper">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-actions">
              <button
                type="button"
                className="reset-filters-btn"
                onClick={resetFilters}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        <div className="table-container" ref={tableContainerRef}>
          <table className="data-table">
            <thead>
              <tr>
                {visibleColumns.map((col) => {
                  const sortableColumns = [
                    "iccid",
                    "imsi",
                    "status",
                    "installed",
                    "provider_id",
                    "user_id",
                    "created_at",
                    "updated_at",
                    "order_date",
                    "last_order_date",
                    "last_package_active_before",
                    "data_usage",
                    "data_limit",
                    "packages_number",
                    "data_usage_percent",
                    "last_package_name",
                  ];
                  const sortable = sortableColumns.includes(col.key);
                  return (
                    <th
                      key={col.key}
                      onClick={sortable ? () => handleSort(col.key) : undefined}
                      style={{ cursor: sortable ? "pointer" : "default" }}
                    >
                      <span className="th-inner">
                        <span className="th-label">{col.label}</span>
                        {sortBy === col.key && (
                          <img
                            src={sortOrder === "asc" ? sortUp : sortDown}
                            alt=""
                            className="sort-icon"
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>

              <tr className="filter-row">
                {visibleColumns.map((col) => (
                  <th key={`${col.key}-filter`}>
                    {(() => {
                      if (nonFilterableColumns.includes(col.key)) {
                        return (
                          <input type="text" placeholder="No filter" disabled />
                        );
                      }

                      if (dateRangeColumns.includes(col.key)) {
                        return (
                          <div className="filter-minmax-group">
                            <input
                              type="date"
                              placeholder="Min"
                              value={filters[`min_${col.key}`] || ""}
                              onChange={(e) =>
                                onFilterChange(`min_${col.key}`, e.target.value)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                            <input
                              type="date"
                              placeholder="Max"
                              value={filters[`max_${col.key}`] || ""}
                              onChange={(e) =>
                                onFilterChange(`max_${col.key}`, e.target.value)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        );
                      }

                      if (numericRangeColumns.includes(col.key)) {
                        return (
                          <div className="filter-minmax-group">
                            <input
                              type="number"
                              placeholder="Min"
                              value={filters[`min_${col.key}`] || ""}
                              onChange={(e) =>
                                onFilterChange(`min_${col.key}`, e.target.value)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                            <input
                              type="number"
                              placeholder="Max"
                              value={filters[`max_${col.key}`] || ""}
                              onChange={(e) =>
                                onFilterChange(`max_${col.key}`, e.target.value)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        );
                      }

                      if (col.key === "installed") {
                        return (
                          <select
                            value={filters.installed}
                            onChange={(e) =>
                              onFilterChange("installed", e.target.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="">All</option>
                            <option value="true">Installed</option>
                            <option value="false">Not Installed</option>
                          </select>
                        );
                      }

                      if (
                        [
                          "created_at",
                          "updated_at",
                          "order_date",
                          "last_order_date",
                          "last_package_active_before",
                        ].includes(col.key)
                      ) {
                        return (
                          <input
                            type="date"
                            value={filters[col.key] || ""}
                            onChange={(e) =>
                              onFilterChange(col.key, e.target.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        );
                      }

                      if (col.key === "status") {
                        return (
                          <select
                            value={filters.status}
                            onChange={(e) =>
                              onFilterChange("status", e.target.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="">All</option>
                            {statusOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt.charAt(0).toUpperCase() + opt.slice(1)}
                              </option>
                            ))}
                          </select>
                        );
                      }

                      return (
                        <input
                          type="text"
                          placeholder={``}
                          value={filters[col.key] || ""}
                          onChange={(e) =>
                            onFilterChange(col.key, e.target.value)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      );
                    })()}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1}>Loading...</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1}>No results</td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.iccid}>
                    {visibleColumns.map((col) => {
                      let val = item[col.key];

                      if (col.key === "installed") {
                        val =
                          val === true ? (
                            <span className="badge-active">Installed</span>
                          ) : (
                            <span className="badge-inactive">
                              Not Installed
                            </span>
                          );
                      } else if (
                        col.key === "created_at" ||
                        col.key === "updated_at" ||
                        col.key === "order_date" ||
                        col.key === "last_order_date" ||
                        col.key === "last_package_active_before"
                      ) {
                        val = formatDate(val);
                      } else if (
                        col.key === "provider_id" ||
                        col.key === "data_usage" ||
                        col.key === "data_limit" ||
                        col.key === "packages_number"
                      ) {
                        val = formatNumberNoDecimal(val);
                      } else if (col.key === "data_usage_percent") {
                        if (val !== null && val !== undefined) {
                          val = `${Number(val).toFixed(2)}%`;
                        } else {
                          val = "-";
                        }
                      } else if (
                        val === null ||
                        val === undefined ||
                        val === ""
                      ) {
                        val = "-";
                      } else if (typeof val === "number") {
                        val = val.toFixed(2);
                      }
                      return <td key={col.key}>{val}</td>;
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination-bar-wide">
          <button
            className="pagination-btn left"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <span className="pagination-center">Page {page}</span>
          <button
            className="pagination-btn right"
            disabled={items.length < pageSize}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </section>
    </main>
  );
}
